// Citação no topo da bolha (o cliente, ou a loja, respondeu a uma mensagem específica):
// autor + trecho, miniatura quando o original é imagem/figurinha, e "Mensagem não
// disponível" quando o original não está no banco. Clicável só na thread ao vivo,
// onde a rolagem até o original existe (`onOpen`).
import { cn } from '@/lib/utils/cn';
import {
  isQuoteUnavailable,
  type MessageReplyTo,
  type MessageType,
  type QuotedMessage,
} from '../api';
import styles from './MessageBubbleParts.module.css';

const TYPE_LABEL: Partial<Record<MessageType, string>> = {
  IMAGE: '📷 Imagem',
  AUDIO: '🎤 Áudio',
  VIDEO: '🎥 Vídeo',
  FILE: '📄 Arquivo',
  STICKER: 'Figurinha',
  CONTACTS: '👤 Contato',
  QUOTE: 'Orçamento',
  TEMPLATE: 'Template',
};

/** Quem escreveu a mensagem citada, no mesmo vocabulário do rótulo da bolha. */
export function quotedAuthor(
  q: Pick<QuotedMessage, 'direction' | 'source' | 'authorName'>,
): string {
  if (q.direction === 'INBOUND') return q.authorName ?? 'Cliente';
  if (q.source === 'AI') return 'IA';
  return q.authorName ?? 'Loja';
}

/** Trecho curto: o texto, ou o tipo da mídia quando não há legenda. */
export function quotedSnippet(q: Pick<QuotedMessage, 'type' | 'text'>): string {
  const text = q.text?.trim();
  if (text) return text;
  return TYPE_LABEL[q.type] ?? 'Mensagem';
}

interface Props {
  replyTo: MessageReplyTo;
  /** Rola até o original. Ausente = citação só de leitura (histórico, prévia). */
  onOpen?: (messageId: string) => void;
}

export function QuotedBlock({ replyTo, onOpen }: Props) {
  if (isQuoteUnavailable(replyTo)) {
    return (
      <div className={cn(styles.quoted, styles.quotedUnavailable)}>
        <span className={styles.quotedText}>Mensagem não disponível</span>
      </div>
    );
  }

  const author = quotedAuthor(replyTo);
  const snippet = quotedSnippet(replyTo);
  const thumb =
    (replyTo.type === 'IMAGE' || replyTo.type === 'STICKER') && replyTo.mediaUrl
      ? replyTo.mediaUrl
      : null;
  const body = (
    <>
      <span className={styles.quotedBody}>
        <span className={styles.quotedAuthor}>{author}</span>
        <span className={styles.quotedText}>{snippet}</span>
      </span>
      {thumb && <img className={styles.quotedThumb} src={thumb} alt="" loading="lazy" />}
    </>
  );

  if (!onOpen) {
    return (
      <div className={styles.quoted} data-direction={replyTo.direction}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={cn(styles.quoted, styles.quotedButton)}
      data-direction={replyTo.direction}
      onClick={() => onOpen(replyTo.id)}
      aria-label={`Ir para a mensagem citada de ${author}: ${snippet}`}
    >
      {body}
    </button>
  );
}
